import { useCallback, useEffect, useRef, useState } from "react"

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet"
import welcomeImg from "@/assets/textures/tournament-welcome.png"
import { useI18n } from "@/shared/i18n"
import { useTournamentStore, useTournamentWelcomeStore } from "@/shared/store"
import { formatTournamentStartsAt } from "@/shared/lib/formatTournamentStartsAt"
import {
  isTournamentWelcomeSeen,
  markTournamentWelcomeSeen,
} from "@/shared/lib/tournamentWelcomeSeen"

import { TournamentFlipCountdown } from "./TournamentFlipCountdown"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

/**
 * Global tournament welcome bottom sheet (mounted once in `App`, built on the
 * shared `Sheet` primitive used across the app).
 *
 * Two states driven by wall-clock vs the tournament window:
 *   - before `startsAt` → "Tournament ahead!" + "until start" countdown to start;
 *   - after `startsAt`  → "First tournament started!" + "until end" countdown to end.
 *
 * Auto-opens once per tournament id (persisted in localStorage); a brand-new
 * tournament re-triggers it. The CTA / X / backdrop all just close it. The
 * emulator can drive it imperatively via `useTournamentWelcomeStore.show`.
 */
export function TournamentWelcomeModal() {
  const { t, locale } = useI18n()
  const tournament = useTournamentWelcomeStore((s) => s.tournament)
  const isOpen = useTournamentWelcomeStore((s) => s.isOpen)
  const openSource = useTournamentWelcomeStore((s) => s.openSource)
  const close = useTournamentWelcomeStore((s) => s.close)
  const show = useTournamentWelcomeStore((s) => s.show)

  // Auto-open for an unseen, not-finished active tournament. Keyed on id +
  // finished flag so realtime updates (new object refs) don't re-run it.
  const liveTournamentId = useTournamentStore((s) => s.summary?.id ?? null)
  const liveTournamentFinished = useTournamentStore(
    (s) => s.summary?.isFinished ?? false
  )
  useEffect(() => {
    if (!liveTournamentId || liveTournamentFinished) return
    if (isTournamentWelcomeSeen(liveTournamentId)) return
    const live = useTournamentStore.getState().summary
    if (!live) return
    markTournamentWelcomeSeen(liveTournamentId)
    show(live, { open_source: "auto" })
  }, [liveTournamentId, liveTournamentFinished, show])

  const welcomeViewTrackedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen || !tournament) {
      if (!isOpen) welcomeViewTrackedRef.current = null
      return
    }
    const trackKey = `${tournament.id}:${openSource ?? "manual"}`
    if (welcomeViewTrackedRef.current === trackKey) return
    welcomeViewTrackedRef.current = trackKey
    const phase =
      tournament.isUpcoming || Date.now() < tournament.startsAt
        ? "upcoming"
        : "active"
    trackAnalyticsEvent(AnalyticsEvent.TournamentWelcomeView, {
      tournament_id: tournament.id,
      phase,
      open_source: openSource ?? "manual",
    })
  }, [isOpen, tournament, openSource])

  // Bumped when the "until start" countdown reaches 0, to re-derive the mode.
  const [, setTick] = useState(0)
  const handleCountdownComplete = useCallback(() => setTick((n) => n + 1), [])

  if (!tournament) return null

  const isUpcoming = tournament.isUpcoming || Date.now() < tournament.startsAt
  const target = isUpcoming ? tournament.startsAt : tournament.endsAt
  const title = isUpcoming
    ? t("tournamentWelcome.upcomingTitle")
    : t("tournamentWelcome.startedTitle")
  const timerLabel = isUpcoming
    ? t("tournamentWelcome.untilStart")
    : t("tournamentWelcome.untilEnd")

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close()
      }}
    >
      <SheetContent
        side="bottom"
        className="flex flex-col gap-0 overflow-hidden rounded-t-3xl border-white/[0.04] bg-[#24242E] p-0"
        closeClassName="text-white/90"
      >
        {/* Cover */}
        <div className="self-stretch p-2">
          <img
            src={welcomeImg}
            alt=""
            className="h-[200px] w-full rounded-2xl object-cover"
          />
        </div>

        {/* Title + description */}
        <div className="flex flex-col gap-2 px-4 py-2">
          <SheetTitle className="w-full text-left text-2xl font-semibold leading-[29px] tracking-[-0.5px] text-white">
            {title}
          </SheetTitle>
          {isUpcoming && (
            <p className="w-full text-left text-sm font-medium leading-[1.3] tracking-[-0.5px] text-green-100">
              {t("tournamentWelcome.startsAt", {
                date: formatTournamentStartsAt(tournament.startsAt, locale),
              })}
            </p>
          )}
          <SheetDescription className="w-full text-left text-base leading-[1.3] tracking-[-0.5px] text-[#A0A0AB]">
            {t("tournamentWelcome.description")}
          </SheetDescription>
        </div>

        {/* Timer */}
        <div className="self-stretch px-4 py-1">
          <div className="flex items-center gap-2 self-stretch rounded-2xl bg-white/10 py-2.5 pl-4 pr-2.5">
            <span className="flex-1 text-lg font-semibold leading-[22px] tracking-[-0.5px] text-white">
              {timerLabel}
            </span>
            <TournamentFlipCountdown
              to={target}
              onComplete={handleCountdownComplete}
            />
          </div>
        </div>

        {/* CTA */}
        <div className="self-stretch p-4">
          <button
            type="button"
            onClick={close}
            className="flex h-12 w-full items-center justify-center rounded-2xl bg-green-100 text-base font-bold tracking-[-0.5px] text-black-100 transition-opacity active:opacity-80"
          >
            {t("tournamentWelcome.cta")}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
